import React, { useState } from 'react';
import type { Modification } from '../types/modification';
import { categories } from '../utils/categories';
import { VendorCombobox } from './VendorCombobox';
import { CustomDatePicker } from './CustomDatePicker';
import CustomDropdown from './CustomDropdown';
import { specificationsForStorage } from '../utils/specifications';
import { ModPhotoField } from './vehicle-detail/ModPhotoField';

interface Vendor { name: string; }
interface AddModificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (modification: Omit<Modification, 'id'>, photoFile?: File | null) => void;
  existingModifications: Modification[];
  vendors: Vendor[];
  refreshVendors: () => Promise<void>;
  /** Prefill parent when adding a part inside an assembly */
  initialParent?: { id?: string; title: string; category?: string } | null;
}

export const AddModificationModal: React.FC<AddModificationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  existingModifications,
  vendors,
  refreshVendors,
  initialParent = null,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    cost: '',
    installDate: '',
    vendor: '',
    partNumber: '',
    notes: '',
    parentComponent: '',
    parentModificationId: '',
    specifications: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    if (initialParent?.title) {
      setFormData((prev) => ({
        ...prev,
        parentComponent: initialParent.title,
        parentModificationId: initialParent.id ? String(initialParent.id) : '',
        category: initialParent.category || prev.category,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        parentComponent: '',
        parentModificationId: '',
      }));
    }
  }, [isOpen, initialParent]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.cost && isNaN(Number(formData.cost))) {
      newErrors.cost = 'Cost must be a valid number';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const specLines = specificationsForStorage(formData.specifications);

      // Build with camelCase for TS
      const modification: Omit<Modification, 'id'> = {
        title: formData.title.trim(),
        category: formData.category.trim(),
        description: formData.description.trim() || undefined,
        cost: formData.parentComponent.trim() || formData.parentModificationId
          ? 0
          : formData.cost
            ? Number(formData.cost)
            : undefined,
        installDate: formData.installDate || undefined,
        vendor: formData.vendor.trim() || undefined,
        partNumber: formData.partNumber.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        parentComponent: formData.parentComponent.trim() || undefined,
        specifications: specLines.length > 0 ? specLines : undefined
      };

      // Map camelCase to snake_case for backend (use 'any' to avoid TS errors)
      const backendModification: any = {
        ...modification,
        install_date: modification.installDate,
        part_number: modification.partNumber,
      };
      if (formData.parentModificationId) {
        backendModification.parent_modification_id = parseInt(formData.parentModificationId, 10);
      }
      delete backendModification.installDate;
      delete backendModification.partNumber;

      onSubmit(backendModification, photoFile);
      await refreshVendors();

      // Reset form
      setFormData({
        title: '',
        category: '',
        description: '',
        cost: '',
        installDate: '',
        vendor: '',
        partNumber: '',
        notes: '',
        parentComponent: '',
        parentModificationId: '',
        specifications: ''
      });
      setPhotoFile(null);
      setErrors({});
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={handleBackdropClick}
    >
      <div className="modal-panel max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
          <h2 className="heading-card">
            {initialParent?.title ? 'Add part inside' : 'Add New Modification'}
          </h2>
          <button
            onClick={onClose}
            className="btn-icon"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Required Fields */}
          <div>
            <h3 className="heading-card mb-4">Required Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="input-label">
                  Title *
                </label>
                <input
                  type="text"
                  data-testid="mod-title"
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className={`input ${errors.title ? 'border-red-500' : ''}`}
                  placeholder="e.g., Garrett GT2860RS Turbocharger"
                />
                {errors.title && <p className="input-error">{errors.title}</p>}
              </div>

              <div>
                <label className="input-label">
                  Category *
                </label>
                <CustomDropdown
                  value={formData.category}
                  onChange={(value) => updateField('category', value)}
                  options={categories.map((cat: { name: string }) => ({
                    value: cat.name,
                    label: cat.name
                  }))}
                  placeholder="Select a category"
                  error={!!errors.category}
                  data-testid="mod-category"
                />
                {errors.category && <p className="input-error">{errors.category}</p>}
              </div>
            </div>
          </div>

          {/* Optional Fields */}
          <div>
            <h3 className="heading-card mb-4">Additional Details</h3>
            <div className="space-y-4">
              <div>
                <label className="input-label">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="Brief description of the modification..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">
                    Cost ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cost}
                    onChange={(e) => updateField('cost', e.target.value)}
                    className={`input ${errors.cost ? 'border-red-500' : ''}`}
                    placeholder="0.00"
                  />
                  {errors.cost && <p className="input-error">{errors.cost}</p>}
                </div>

                <div>
                  <CustomDatePicker
                    label="Install Date"
                    value={formData.installDate}
                    onChange={(date) => updateField('installDate', date)}
                    placeholder="Select install date..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">
                    Vendor
                  </label>
                  <VendorCombobox
                    value={formData.vendor}
                    onChange={(value) => updateField('vendor', value)}
                    placeholder="Search or add vendor..."
                    vendors={vendors.map(v => v.name)}
                  />
                </div>

                <div>
                  <label className="input-label">
                    Part Number
                  </label>
                  <input
                    type="text"
                    value={formData.partNumber}
                    onChange={(e) => updateField('partNumber', e.target.value)}
                    className="input"
                    placeholder="e.g., 466541-5004S"
                  />
                </div>
              </div>

              <div>
                <label className="input-label">
                  Parent Component
                </label>
                <CustomDropdown
                  value={formData.parentComponent}
                  onChange={(value) => updateField('parentComponent', value)}
                  options={[
                    { value: '', label: 'Select parent component (optional)' },
                    ...existingModifications.map(mod => ({
                      value: mod.title,
                      label: mod.title
                    }))
                  ]}
                  placeholder="Select parent component (optional)"
                />
                <p className="input-helper">
                  Select if this modification is part of a larger component or system<br></br>
                  (If parent selected, will set cost to $0.00)
                </p>
              </div>

              <div>
                <label className="input-label">
                  Specifications
                </label>
                <textarea
                  value={formData.specifications}
                  onChange={(e) => updateField('specifications', e.target.value)}
                  rows={4}
                  className="input"
                  placeholder={'Final drive: 4.308\nRear differential: 3.309\nGear ratios: 3.000, 2.000, 1.471, 1.111, 0.857'}
                />
                <p className="input-helper">
                  Add one detail per line.
                </p>
              </div>

              <div>
                <ModPhotoField value={photoFile} onChange={setPhotoFile} />
              </div>

              <div>
                <label className="input-label">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="Additional notes or comments..."
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-white/[0.08]">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              data-testid="mod-submit"
            >
              Add Modification
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 